import { format } from 'date-fns'
import React, { PropsWithChildren, useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useModal } from '@context/ModalsContext'

import { Heading6 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { PopoverInput } from '@ui/components/Popover'
import { FileIcon } from '@ui/icons/File.icon'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { getDateFromDay, getDateRangeOfLastYear } from '@utils/date.utils'
import { getAvailableDates, getYearSplitter } from '@utils/statements'

import { StatementType } from '@model/Statements'

import { useFetchOne } from '@hooks/useFetch'
import { ScrollToIds, useScrollToElementIds } from '@hooks/useScrollToElementIds'

import { Box } from '@shared/components/Box'
import { FieldWrapper } from '@shared/components/FieldWrapper'
import { Loading } from '@shared/components/Loading/Loading'

import { DownloadedStatement } from './DownloadedStatement'
import { useStatementsTAId } from './StatementsETD'
import { PeriodModal } from './modals/Period.modal'

import styles from './Statement.module.scss'

const useStatementFetch = (statementType: StatementType) => {
  const { tradingAccountId } = useStatementsTAId()
  const apiClient = useApiClient(ClientApiClient)

  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [month, setMonth] = useState<number>()
  const [day, setDay] = useState<number>()
  const [downloaded, setDownloaded] = useState(false)

  const availableDatesCallback = useCallback(async () => {
    if (!tradingAccountId) {
      return
    }
    try {
      setLoading(true)

      const monthData = getDateRangeOfLastYear()
      const dateRangeStart = format(monthData.dateRangeStart, 'yyyy-MM-dd')
      const dateRangeEnd = format(monthData.dateRangeEnd, 'yyyy-MM-dd')
      const response = await apiClient.checkTradingAccountStatement(
        tradingAccountId,
        statementType,
        { dateRangeStart, dateRangeEnd }
      )
      return response.dates
    } finally {
      setLoading(false)
    }
  }, [tradingAccountId, apiClient])
  const { data: availableDatesFetch } = useFetchOne(availableDatesCallback)

  const availableDates = useMemo(
    () => getAvailableDates(t, availableDatesFetch),
    [availableDatesFetch, t]
  )

  const dayOptions = useMemo(
    () => availableDates?.availableDayOptions.find((option) => option.month === month)?.days || [],
    [availableDates, month]
  )

  const statementsCallback = useCallback(async () => {
    if (month && tradingAccountId) {
      try {
        setLoading(true)
        const fromDay = availableDates?.availableDayOptions.find((d) => d.month === month)
          ?.days?.[0].id
        const monthDate = getDateFromDay(month, day || fromDay || 1)
        const date = format(monthDate, 'yyyy-MM-dd')
        const response = await apiClient.getTradingAccountStatement(
          tradingAccountId,
          statementType,
          {
            date,
          }
        )
        setDownloaded(true)
        window.location.assign(response.url)
      } finally {
        setLoading(false)
      }
    }
  }, [apiClient, availableDates?.availableDayOptions, day, month, tradingAccountId])

  return {
    dayOptions,
    availableDates,
    loading,
    setLoading,
    month,
    setMonth,
    day,
    setDay,
    statementsCallback,
    downloaded,
    setDownloaded,
    tradingAccountId,
  }
}

export const StatementDailyPage: React.FC = () => <StatementPage daily />

export const StatementMonthlyPage: React.FC = () => <StatementPage />

const StatementPage: React.FC<{ daily?: boolean }> = ({ daily = false }) => {
  const statementFetchProps = useStatementFetch(daily ? StatementType.Daily : StatementType.Monthly)
  const { downloaded, availableDates, loading, month, day, dayOptions, tradingAccountId } =
    statementFetchProps

  const { setDay, setMonth, setDownloaded } = statementFetchProps

  const { t } = useTranslation()

  const monthModal = useModal(PeriodModal, {
    title: t('Statements.Select Month'),
    splitter: getYearSplitter(availableDates?.availableMonthOptions || []),
    options: availableDates?.availableMonthOptions || [],
    initialValue: month,
    onChangeOption: setMonth,
  })

  const dayModal = useModal(PeriodModal, {
    title: t('Statements.Select Day'),
    options: dayOptions,
    initialValue: day,
    onChangeOption: setDay,
  })

  useScrollView(statementFetchProps)

  if (downloaded) {
    return <DownloadedStatement goBack={() => setDownloaded(false)} />
  }

  const dailyCondition = !day || !tradingAccountId
  const monthlyCondition = !month || !tradingAccountId

  return (
    <StatementTemplate
      isLoading={loading}
      disabled={daily ? dailyCondition : monthlyCondition}
      onSubmit={statementFetchProps.statementsCallback}
    >
      <FieldWrapper direction='row'>
        <SelectMonth {...statementFetchProps} onClick={monthModal.open} onSetMonth={setMonth} />
        {daily && <SelectDay {...statementFetchProps} onClick={dayModal.open} onSetDay={setDay} />}
      </FieldWrapper>
    </StatementTemplate>
  )
}

interface SelectProps extends ReturnType<typeof useStatementFetch> {
  onClick(): void
  onSetMonth?(month: number): void
  onSetDay?(day: number): void
}

const SelectMonth: React.FC<SelectProps> = (props) => {
  const { availableDates, month, loading, onSetMonth } = props

  const { t } = useTranslation()

  return (
    <PopoverInput
      value={month}
      label={t('Statements.Select Month')}
      options={availableDates?.availableMonthOptions?.map(({ id, label }) => ({
        id,
        name: label,
      }))}
      disabled={
        !availableDates?.availableMonthOptions.length || availableDates?.lockMonths || loading
      }
      required
      onChange={(option) => onSetMonth?.(option.id)}
    />
  )
}

const SelectDay: React.FC<SelectProps> = (props) => {
  const { day, month, loading, onSetDay, dayOptions } = props

  const { t } = useTranslation()

  return (
    <PopoverInput
      label={t('Statements.Select Day')}
      options={dayOptions?.map(({ id, label }) => ({
        id,
        name: label,
      }))}
      value={day}
      disabled={!month || loading}
      onChange={(option) => onSetDay?.(option.id)}
    />
  )
}

const useScrollView = (props: ReturnType<typeof useStatementFetch>) => {
  const { downloaded } = props

  const { scrollIntoView } = useScrollToElementIds()

  useLayoutEffect(() => {
    scrollIntoView([ScrollToIds.StatementsETDHeader])
  }, [downloaded, scrollIntoView])
}

interface StatementTemplateProps {
  isLoading: boolean
  disabled: boolean
  onSubmit(): void
}

const StatementTemplate: React.FC<PropsWithChildren<StatementTemplateProps>> = (props) => {
  const { isLoading, disabled, children, onSubmit } = props

  const { t } = useTranslation()

  return (
    <div className={styles.statement}>
      <div className={styles.reportIconWrapper}>
        <FileIcon size={64} color='text-base-em' />
      </div>
      <Heading6 className={styles.title} medium>
        {t('Statements.Download Statement')}
      </Heading6>
      <Loading isLoading={isLoading} showLoadingIcon>
        <div className={styles.selectorsWrapper}>{children}</div>
        <Box mt='x8'>
          <Button.Primary size='lg' loading={isLoading} disabled={disabled} onClick={onSubmit}>
            {t('Statements.Download Statement')}
          </Button.Primary>
        </Box>
      </Loading>
    </div>
  )
}
